import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import Chip from '@mui/material/Chip';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import { Button, Icon, IconButton, Tooltip } from '@mui/material';
import { Controller, useFormContext } from 'react-hook-form';
import { useSelector } from 'react-redux';
import FuseUtils from '@fuse/utils/FuseUtils';
import { authPermissions } from 'app/auth';
import { useLocation } from 'react-router-dom';
import ValueSectionSmall from './ValueSectionSmall';
import { SandblastedAtPlantChip } from './SandblastedAtPlant';
import { formatDateString, selectAmount, selectAndFormatWeight, formatter, displayName, getOrderCustomerDisplay, getOrderCustomerLabel } from './AuraFunctions';
import _ from '../../../../@lodash';


function OrderHeader(props) {
  const methods = useFormContext();
  const { control, formState } = methods;
  const { errors } = formState;
  const currentUser = useSelector(({ user }) => user);
  const { role } = currentUser
  const isManage = currentUser && FuseUtils.hasPermission(authPermissions.manage, currentUser.role);
  const location = useLocation()
  const { pathname } = location
  const isSupplierInvoice = pathname.split('/').includes('supplier-invoices')
  const urlParams = new URLSearchParams(location.search);
  const sidValue = urlParams.get('sid');
  // Determine the label for the "Paid Amount" field. "Paid Amount" for supplier, else displayname value
  const paidAmtLabel = isSupplierInvoice ? "Paid Amount" : displayName?.invoicePaidAmt;

  const {
    order: orderData,
    isShipmentData,
    isCommunicationTab,
    hideAmount = false,
    hideQuantity = false,
    hideDate = false,
    hideCustomer = false,
    showTotalWeight,
    showPrintAction,
    showDownloadAction,
    onPrint,
    onDownload,
    stage,
    overRideData = {},
    hideShipmentDetails,
    showSupplierName,
    isPrinting,
    showContainerNo,
    hideCTNAndCompany,
    reviewNoteDialog = false,
    pageType = null,
    showShipmentType = null,
    confirmInvoiceShipTo = false,
    showInvoiceAmounts = null,
    showVAS = false,
    handleVasPreview = null,
    hasValidSurcharge,
    showRevisionHistory, // added field to show Revision History Button in choose supplier
    handleRevisionHistoryPreview = null, // on preview used to display revision history
    isContainerManagement // added flag to  hide container-management stage view
  } = props;
  const isOutboundShipment = pageType === "outbound_shipments" || pageType === "outbound_shipments_customer"

  if (!orderData) {
    return null;
  }
  const getOrder = () => {
    let order = {}
    if (orderData.invoice_no) {
      order = {
        order_no: orderData.invoice_no,
        amount: orderData.invoice_amount,
        invoice_amount: orderData.invoice_amount,
        paid_amount: orderData.paid_amount,
        due_amount: orderData.due_amount,
        // PGSS-4886: Tariffs & Customs Fee from Confirm & Invoice
        tax: orderData.tax,
        shipping_cost: orderData.shipping_cost,
        date: orderData.invoice_date,
        company_name: orderData.customer_name || orderData.company_name,
        supplier_name: orderData?.supplier?.name || orderData?.supplier_name || '-',
        status: {
          name: orderData.status?.name
        },
        shipment_type: orderData.shipment_type,
        pallet_count: orderData.pallet_count,
        ship_to: orderData.ship_to ?? orderData.shipTo,
        ship_to_rows: orderData.ship_to_rows,
        ship_to_multiple: orderData.ship_to_multiple,
      }
    }
    else if (['shipment'].includes(stage) && isOutboundShipment){
      order = {
        ...orderData,
        order_no: orderData.shipment_no,
        date: orderData.shipment_date,
        quantity: orderData.quantity
      }
    }
    else if (['shipment', 'in-production','container-management'].includes(stage)) {
      order = {
        ...orderData,
        order_no: orderData.container_number,
        date: orderData.shipment_date,
        quantity: orderData.no_of_order_items
      }
    }
    else {
      return { ...orderData, ...overRideData }
    }
    return order
  }

  const getOrderLabel = () => {
    if (['confirmed', 'fulfilled'].includes(stage)) {
      return 'Order'
    }
    if (stage === 'in-production' || stage === 'container-management') {
      return 'Container'
    }
    if (stage === 'rfd') {
      return 'Rfp'
    }
    if (stage === 'completed') {
      return 'Rfp Completed'
    }
    return _.capitalize(stage)
  }

  const getDateLabel = () => {
    if (stage === 'rfd') {
      return 'Rfp'
    }
    if(stage === 'container-management'){
      return 'Container'
    }
    if (orderData?.status?.system_code === 'ORDER_RECEIVED') {
      return 'Received Order '
    }
    if (orderData?.status?.system_code === 'RFD_SUBMITTED') {
      return 'RFP Completed'
    }
    return _.capitalize(stage)
  }

  const statusName = _.capitalize(getOrder().status?.name || getOrder().status || getOrder()['status.name'] || getOrder()[0]?.status.name)

  return (
    <>
      <Box boxShadow={2}>
        <div className="flex flex-wrap w-full items-center px-8 py-4 md:px-16 border-b">
          <div className="flex flex-col flex-auto my-12 mx-8">
            <div className="flex items-center space-x-20">
              <Typography className="font-mono text-md">{`${getOrderLabel()} No :`}{isShipmentData ? getOrder()?.orders[0].order_no : getOrder().order_no || getOrder().invoice_no}</Typography>
              {!hideDate && (!['staff', 'admin', 'sales'].includes(role) || reviewNoteDialog || pageType === "outbound_shipments_customer") && (
                <Typography
                  className="font-mono text-md leading-none tracking-tight"
                  color="text.secondary"
                >
                  <span className="font-bold">{['shipment', 'in-production'].includes(stage) ? 'Shipment Date' : `${getDateLabel()} Date:`} </span>  {formatDateString(getOrder().date)}
                </Typography>
              )}
              {!isShipmentData && !isPrinting && statusName && <Chip
                color="info"
                size="small"
                label={statusName}
              />}
              {!isShipmentData && <SandblastedAtPlantChip order={getOrder()} />}
            </div>
            <div className="flex  items-end mt-4 space-x-6">
              {!hideCustomer && !isSupplierInvoice && sidValue !== "all" && !isContainerManagement && <Typography className="font-mono text-md tracking-wider" color="text.secondary">
                <span className="font-bold">{getOrderCustomerLabel(role)}: </span>{isShipmentData ? getOrder()?.orders[0].customer.company_name : getOrderCustomerDisplay(role, orderData, { isCommunicationTab })}
              </Typography>}

            </div>
            <div className="flex  items-end mt-4 space-x-6">
              {showSupplierName && getOrder()?.supplier_name && !['customer'].includes(role) && !['supplier'].includes(role) && !isPrinting && <Typography className="font-mono text-md tracking-wider" color="text.secondary">
                <span className="font-bold">Supplier Name: </span>  {getOrder()?.supplier_name}
              </Typography>}
            </div>

            <div className="flex items-end mt-4 space-x-6">
              {getOrder()?.ship_to && role === 'customer' && ['rfq'].includes(stage) && ( <Typography className="font-mono text-md tracking-wider" color="text.secondary">
                 <span className="font-bold">Ship To:</span> {getOrder()?.ship_to}
               </Typography>)}
            </div>

            {!['invoice', 'shipment', 'rfq', 'in-production'].includes(stage) && !['staff', 'admin'].includes(role) && (
              <>
                {role === 'supplier' && !['quote', 'rfq'].includes(stage) && (
                  <>
                    {!['container-management'].includes(stage) &&
                      <div className="flex items-end mt-4 space-x-6">
                        <Typography className="font-mono text-md tracking-wider" color="text.secondary">
                          <span className="font-bold"> Delivery Rail Ramp: </span>{getOrder().delivery_location
                          }
                        </Typography>
                      </div>
                    }
                    <div className="flex items-end mt-4 space-x-20">
                      <Typography className="font-mono text-md tracking-wider" color="text.secondary">
                        <span className="font-bold"> Order Type: </span> {getOrder().order_type}
                      </Typography>
                    </div></>
                )}

              </>
            )}
             {/* {showOrderType && (
              <div className="flex items-end mt-4 space-x-20">
                <Typography className="font-mono text-md tracking-wider" color="text.secondary">
                  <span className="font-bold"> Order Type: </span> {getOrder().order_type}
                </Typography>
              </div>
            )} */}
            {showShipmentType && (
              <div className="flex w-full flex-col">
                <div className="flex flex-col md:flex-row md:gap-20">
                  {["shipment"].includes(stage) && isOutboundShipment && (
                    <div className="flex items-end md:mt-4">
                      <Typography
                        className="font-mono text-md tracking-wider"
                        color="text.secondary"
                      >
                        <span className="font-bold"> Shipment Date: </span>{" "}
                        {formatDateString(getOrder().shipment_date)}
                      </Typography>
                    </div>
                  )}
                  {getOrder().shipment_type && (
                    <>
                      <div className="flex items-end md:mt-4">
                        <Typography
                          className="font-mono text-md tracking-wider"
                          color="text.secondary"
                        >
                          <span className="font-bold"> Shipment Type: </span>{" "}
                          {getOrder().shipment_type || "-"}
                        </Typography>
                      </div>
                      <div className="flex items-end md:mt-4">
                        <Typography
                          className="font-mono text-md tracking-wider"
                          color="text.secondary"
                        >
                          <span className="font-bold"> No of Pallets: </span>{" "}
                          {getOrder().pallet_count || "-"}
                        </Typography>
                      </div>
                    </>
                  )}
                  {/* Carrier tracking for the outbound leg — links to the carrier's tracking page
                      when one is configured for that carrier. */}
                  {getOrder().tracking_number && (
                    <div className="flex items-end md:mt-4">
                      <Typography
                        className="font-mono text-md tracking-wider"
                        color="text.secondary"
                      >
                        <span className="font-bold">
                          {getOrder().carrier ? `${getOrder().carrier} Tracking #: ` : "Tracking #: "}
                        </span>{" "}
                        {getOrder().tracking_url ? (
                          <a
                            href={getOrder().tracking_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 underline"
                          >
                            {getOrder().tracking_number}
                          </a>
                        ) : (
                          getOrder().tracking_number
                        )}
                      </Typography>
                    </div>
                  )}
                </div>
                {confirmInvoiceShipTo && (
                  <div className="mt-16 flex w-full flex-col space-y-4">
                    {getOrder().ship_to_multiple && Array.isArray(getOrder().ship_to_rows) && getOrder().ship_to_rows.length > 0 ? (
                      <>
                        <Typography className="font-mono text-md tracking-wider" color="text.secondary">
                          <span className="font-bold">Ship to (by order)</span>
                        </Typography>
                        {getOrder().ship_to_rows.map((row, idx) => (
                          <Typography key={row.order_id || idx} className="font-mono text-sm tracking-wider" color="text.secondary">
                            <span className="font-bold">{row.order_no || 'Order'}: </span>
                            {row.ship_to != null && row.ship_to !== '' ? row.ship_to : '—'}
                          </Typography>
                        ))}
                      </>
                    ) : (
                      <Typography className="font-mono text-md tracking-wider" color="text.secondary">
                        <span className="font-bold">Ship to location: </span>
                        {getOrder().ship_to != null && getOrder().ship_to !== '' ? String(getOrder().ship_to) : '—'}
                      </Typography>
                    )}
                  </div>
                )}
              </div>
            )}
            {['shipment'].includes(stage) && !hideShipmentDetails && (
              <>

                <div className="flex flex-col mt-4 space-y-6">
                  {!hideCTNAndCompany && (
                    <>
                      <Typography className="font-mono text-md tracking-wider" color="text.secondary">
                        <span className="font-bold">Container Tracking Number: </span>{getOrder().container_tracking_number}
                      </Typography>
                      <Typography className="font-mono text-md tracking-wider" color="text.secondary">
                        <span className="font-bold">Shipping Company: </span>{getOrder().shipping_company?.name}
                      </Typography>
                    </>
                  )}

                  <Typography className="font-mono text-md tracking-wider" color="text.secondary">
                    <span className="font-bold"> ETA: </span> {formatDateString(getOrder().eta_date)}
                  </Typography>
                </div>
              </>
            )}
          </div>
          {showRevisionHistory && !isPrinting && (
            <div>
              <Button
                size="small"
                color="secondary"
                variant="outlined"
                className="rounded-md"
                onClick={handleRevisionHistoryPreview}
              >
                Revision History
              </Button>
            </div>
          )}
          {showVAS && !isPrinting && (
            <div>
              <Button
                size="small"
                color="secondary"
                variant="outlined"
                className="rounded-md"
                onClick={handleVasPreview}
              >
                View Sandblasting/Etching
              </Button>
            </div>
          )}
          <div>
            <div className='text-right'>
              {showPrintAction &&
                <Tooltip title='Print'>
                  <IconButton onClick={() => onPrint?.()}>
                    <Icon>
                      print
                    </Icon>
                  </IconButton>
                </Tooltip>}
              {showDownloadAction &&
                <Tooltip title='Download'>
                  <IconButton onClick={onDownload}>
                    <Icon>
                      download
                    </Icon>
                  </IconButton>
                </Tooltip>}
            </div>
            <div className="hidden sm:flex items-center mb-8 mx-8 rounded-xl border">
              {(!hideQuantity) && (
                <>
                  <ValueSectionSmall title="Total Quantity" value={isShipmentData ? getOrder()?.orders[0].quantity : getOrder().quantity } />
                  <Divider orientation="vertical" flexItem />
                </>
              )}
              {showTotalWeight === true ? (
                <>
                  <ValueSectionSmall
                    title="Total Weight"
                    isAmount
                    value={selectAndFormatWeight(0, getOrder()?.weight)}
                  />
                  <Divider orientation="vertical" flexItem />
                </>
              ) : null}

              {/* This section displays surcharge only if amount exists */}
              {hasValidSurcharge &&
              !hideAmount &&
              !isShipmentData ? (
                <>
                  <ValueSectionSmall
                    title="Surcharge Amount"
                    isAmount
                    value={formatter.format(
                      selectAmount(
                        getOrder().sur_charge_amount,
                        getOrder().sur_charge_amount
                          ? getOrder().sur_charge_amount
                          : getOrder().sur_charge_amount
                      )
                    )}
                  />
                  <Divider orientation="vertical" flexItem />
                </>
              ) : null}

              {!hideAmount &&
              !isShipmentData && !isContainerManagement &&
              getOrder()?.status?.category !== "rfq_status" ? (
                <>
                  <ValueSectionSmall
                    title="Total Amount"
                    isAmount
                    value={formatter.format(
                      selectAmount(
                        getOrder().all_invoices_pre_tax_total,
                        getOrder().amount
                          ? getOrder().amount
                          : getOrder().displayAmount
                      )
                    )}
                  />
                  <Divider orientation="vertical" flexItem />
                </>
              ) : null}
              {showInvoiceAmounts === true ? (
                <>
                  <ValueSectionSmall
                    title="Invoice Amount"
                    isAmount
                    value={formatter.format(getOrder()?.invoice_amount)}
                  />
                  <Divider orientation="vertical" flexItem />
                  {/* PGSS-4886: always show Tariffs row (matches InvoiceTab / Confirm & Invoice) */}
                  <ValueSectionSmall
                    title={displayName?.tax || 'Tariffs & Customs Fee'}
                    isAmount
                    value={formatter.format(getOrder()?.tax ?? 0)}
                  />
                  <Divider orientation="vertical" flexItem />
                  <ValueSectionSmall
                    title={paidAmtLabel} // Dynamic label based on supplier vs customer invoice
                    isAmount
                    value={formatter.format(getOrder()?.paid_amount)}
                  />
                  <Divider orientation="vertical" flexItem />
                  <ValueSectionSmall
                    title="Due Amount"
                    isAmount
                    value={formatter.format(getOrder()?.due_amount)}
                  />
                  <Divider orientation="vertical" flexItem />
                </>
              ) : null}
              {showContainerNo && (
                <>
                  <ValueSectionSmall
                    title="Container Number"
                    value={getOrder()?.container_number}
                  />
                  <Divider orientation="vertical" flexItem />
                </>
              )}
            </div>
          </div>
        </div>
      </Box>
      {/* <div className="flex flex-col md:flex-row mt-24">
            <div className="flex w-full px-24">
              <Controller
                control={control}
                name="order_no"
                render={({ field }) => (
                  <TextField
                    {...field}
                    className="mb-24"
                    label="Order No *"
                    id="order_no"
                    variant="outlined"
                    value={orderData.order_no}
                    fullWidth
                    size="small"
                    error={!!errors.note}
                    helperText={errors?.note?.message}
                  />
                )}
              />
            </div>
          </div> */}
    </>
  );
}

export default OrderHeader;
